// front/src/pages/ProjectDetailPage.jsx
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

import ProjectTechStack from "../components/projects/ProjectTechStack";
import ProjectVideos from "../components/projects/ProjectVideos";
import ProjectRelated from "../components/projects/ProjectRelated";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const apiBase = process.env.REACT_APP_API_URL || "";

  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 🔥 Lightbox state
  const [lightboxImage, setLightboxImage] = useState(null);

  const loadProject = async () => {
    try {
      const { data } = await axios.get(`${apiBase}/api/projects/${id}`, {
        withCredentials: true,
      });
      setProject(data);
    } catch (err) {
      console.error("Error loading project:", err);
      setError("Error loading project");
    }
  };

  const loadRelated = async () => {
    try {
      const { data } = await axios.get(
        `${apiBase}/api/projects/${id}/related`,
        { withCredentials: true }
      );
      setRelated(data);
    } catch (err) {
      console.error("Error loading related projects:", err);
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);

    (async () => {
      await loadProject();
      await loadRelated();
      setLoading(false);
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="container py-5">
        <p>Loading project...</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">{error || "Project not found"}</div>
        <Link to="/projects" className="btn btn-secondary mt-3">
          Back to projects
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* HERO SECTION */}
      <section
        className="mb-4"
        style={{
          position: "relative",
          backgroundColor: "#111",
          color: "#fff",
        }}
      >
        {project.coverImage?.url && (
          <div
            style={{
              height: "320px",
              backgroundImage: `url(${project.coverImage.url})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "brightness(0.4)",
            }}
          />
        )}

        <div
          className="container"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "flex-end",
            paddingBottom: "2rem",
          }}
        >
          <div>
            <h1 className="display-6">{project.title}</h1>

            {project.category && (
              <span className="badge bg-primary me-2">{project.category}</span>
            )}

            {project.role && (
              <span className="badge bg-secondary">Role: {project.role}</span>
            )}
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <div className="container pb-5">
        <div className="row g-4">
          {/* LEFT COLUMN */}
          <div className="col-lg-8">
            {/* Overview */}
            <section className="mb-4">
              <h2 className="h5 mb-3">Overview</h2>
              <p style={{ whiteSpace: "pre-line" }}>{project.description}</p>
            </section>

            {/* Challenges & Solutions */}
            {(project.challenges || project.solutions) && (
              <section className="mb-4">
                <div className="row g-4">
                  {project.challenges && (
                    <div className="col-md-6">
                      <h3 className="h6 mb-2">Challenges</h3>
                      <p style={{ whiteSpace: "pre-line" }}>
                        {project.challenges}
                      </p>
                    </div>
                  )}

                  {project.solutions && (
                    <div className="col-md-6">
                      <h3 className="h6 mb-2">Solutions</h3>
                      <p style={{ whiteSpace: "pre-line" }}>
                        {project.solutions}
                      </p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Features */}
            {project.features?.length > 0 && (
              <section className="mb-4">
                <h2 className="h5 mb-3">Key Features</h2>
                <ul>
                  {project.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* ---------------- GALLERY WITH FULLSCREEN LIGHTBOX ---------------- */}
            {project.galleryImages?.length > 0 && (
              <section className="mb-4">
                <h2 className="h5 mb-3">Gallery</h2>

                <div className="d-flex flex-wrap gap-3">
                  {project.galleryImages.map((img, i) => (
                    <div
                      key={img.publicId || i}
                      style={{
                        width: "180px",
                        height: "130px",
                        overflow: "hidden",
                        borderRadius: "6px",
                        border: "1px solid #ddd",
                        cursor: "pointer",
                      }}
                      onClick={() => setLightboxImage(img.url || img)}
                    >
                      <img
                        src={img.url || img}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.25s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Videos */}
            <ProjectVideos videos={project.videos || []} />
          </div>

          {/* RIGHT COLUMN */}
          <div className="col-lg-4">
            <section className="mb-4">
              <h2 className="h6 mb-3">Project Info</h2>

              <ProjectTechStack
                techStack={project.techStack || []}
                tools={project.tools || []}
              />

              <div className="mt-3">
                {project.githubUrl && (
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-dark w-100 mb-2"
                  >
                    View on GitHub
                  </a>
                )}

                {project.demoUrl && (
                  <a
                    href={project.demoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-primary w-100"
                  >
                    Live Demo
                  </a>
                )}
              </div>
            </section>

            <ProjectRelated related={related} />
          </div>
        </div>

        <div className="mt-4">
          <Link to="/projects" className="btn btn-outline-secondary btn-sm">
            ← Back to all projects
          </Link>
        </div>
      </div>

      {/* ---------------- FULLSCREEN LIGHTBOX ---------------- */}
      {lightboxImage && (
        <div
          onClick={() => setLightboxImage(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out",
          }}
        >
          <img
            src={lightboxImage}
            alt=""
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              borderRadius: "8px",
              boxShadow: "0 0 20px rgba(255,255,255,0.3)",
              animation: "fadeIn 0.25s ease",
            }}
          />
        </div>
      )}
    </div>
  );
}
